import React, { useState, useEffect } from 'react';
import { Box, InputLabel, useTheme } from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const PhoneNumberInput = ({
  value = '',
  onPhoneChange,
  onCountryChange,
  label = 'Phone Number',
  placeholder = 'Enter phone number',
  margin = 'dense',
  fullWidth = true,
  required = false,
  error = false,
  helperText = ''
}) => {
  const theme = useTheme();
  const [phoneNumber, setPhoneNumber] = useState(value);
  const [detectedCountry, setDetectedCountry] = useState(null);

  useEffect(() => {
    // Try to detect user's country from geolocation
    const detectUserCountry = async () => {
      try {
        // Try to get user's timezone and infer country
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let inferredCountry = null;

        // Simple timezone to country mapping for common cases
        const timezoneCountryMap = {
          'Asia/Karachi': 'PK',
          'America/New_York': 'US',
          'America/Los_Angeles': 'US',
          'America/Chicago': 'US',
          'Europe/London': 'GB',
          'Europe/Paris': 'FR',
          'Europe/Berlin': 'DE',
          'Asia/Dubai': 'AE',
          'Asia/Riyadh': 'SA',
          'Asia/Tokyo': 'JP',
          'Asia/Shanghai': 'CN',
          'Asia/Kolkata': 'IN',
          'Australia/Sydney': 'AU'
        };

        inferredCountry = timezoneCountryMap[timezone] || null;

        if (inferredCountry) {
          setDetectedCountry(inferredCountry);
        }
      } catch (error) {
        console.log('Could not detect user location:', error);
        // Leave detectedCountry as null for no default
      }
    };

    if (!value) {
      detectUserCountry();
    } else if (value !== phoneNumber) {
      setPhoneNumber(value);
      detectCountryFromNumber(value);
    }
  }, [value]);

  const detectCountryFromNumber = (number) => {
    if (!number) return;

    try {
      // Handle 0092 format by converting to +92 format
      let normalizedNumber = number;
      if (number.startsWith('0092')) {
        normalizedNumber = '+92' + number.substring(4);
      } else if (number.startsWith('92') && !number.startsWith('+92')) {
        normalizedNumber = '+' + number;
      }

      const parsed = parsePhoneNumber(normalizedNumber);
      if (parsed && parsed.country) {
        setDetectedCountry(parsed.country);
        // Call the callback to update the parent component's country
        if (onCountryChange) {
          // Convert country code to full country name
          const countryName = getCountryName(parsed.country);
          onCountryChange(countryName);
        }
      }
    } catch (error) {
      console.log('Error parsing phone number:', error);
    }
  };

  const getCountryName = (countryCode) => {
    // Map common country codes to full names used in the form
    const countryMap = {
      'PK': 'Pakistan',
      'US': 'United States',
      'GB': 'United Kingdom',
      'CA': 'Canada',
      'AU': 'Australia',
      'DE': 'Germany',
      'FR': 'France',
      'IT': 'Italy',
      'ES': 'Spain',
      'JP': 'Japan',
      'CN': 'China',
      'IN': 'India',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'RU': 'Russia',
      'ZA': 'South Africa',
      'EG': 'Egypt',
      'NG': 'Nigeria',
      'KE': 'Kenya',
      'AE': 'United Arab Emirates',
      'SA': 'Saudi Arabia',
      'QA': 'Qatar',
      'KW': 'Kuwait',
      'BH': 'Bahrain',
      'OM': 'Oman',
      'JO': 'Jordan',
      'LB': 'Lebanon',
      'SY': 'Syria',
      'IQ': 'Iraq',
      'IR': 'Iran',
      'AF': 'Afghanistan',
      'PK': 'Pakistan',
      'BD': 'Bangladesh',
      'LK': 'Sri Lanka',
      'NP': 'Nepal',
      'MM': 'Myanmar',
      'TH': 'Thailand',
      'VN': 'Vietnam',
      'KH': 'Cambodia',
      'LA': 'Laos',
      'MY': 'Malaysia',
      'SG': 'Singapore',
      'ID': 'Indonesia',
      'PH': 'Philippines',
      'KR': 'South Korea',
      'TW': 'Taiwan',
      'HK': 'Hong Kong',
      'MO': 'Macau'
    };

    return countryMap[countryCode] || countryCode;
  };

  const handlePhoneChange = (value) => {
    setPhoneNumber(value);

    // Detect country from the entered number
    detectCountryFromNumber(value);

    // Call the callback to update parent component
    if (onPhoneChange) {
      onPhoneChange(value || '');
    }
  };

  const customStyles = {
    '& .PhoneInput': {
      display: 'flex',
      alignItems: 'center',
      border: `1px solid rgba(0, 0, 0, 0.23)`,
      borderRadius: '4px',
      padding: '16.5px 14px',
      backgroundColor: theme.palette.background.paper,
      fontSize: '16px',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      minHeight: '56px',
      boxSizing: 'border-box',
      '&:hover': {
        borderColor: theme.palette.text.primary,
      },
      '&:focus-within': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        outline: 'none',
        padding: '15.5px 13px',
      }
    },
    '& .PhoneInputInput': {
      border: 'none',
      outline: 'none',
      fontSize: '16px',
      flex: 1,
      marginLeft: '8px',
      backgroundColor: 'transparent',
      fontFamily: 'inherit',
      '&::placeholder': {
        color: 'rgba(0, 0, 0, 0.6)',
        opacity: 1,
      }
    },
    '& .PhoneInputCountryIcon': {
      width: '24px',
      height: '18px',
      marginRight: '8px',
    },
    '& .PhoneInputCountrySelect': {
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '16px',
      marginRight: '4px',
    },
    '& .PhoneInputCountrySelectArrow': {
      opacity: 0.54,
      marginLeft: '4px',
    }
  };

  return (
    <Box>
      <InputLabel>{label}</InputLabel>
      <Box sx={customStyles} mt={0.5}>
        <PhoneInput
          placeholder={placeholder}
          value={phoneNumber}
          onChange={handlePhoneChange}
          defaultCountry={detectedCountry}
          international={true}
          countryCallingCodeEditable={true}
          addInternationalOption={false}
          withCountryCallingCode={true}
        />
      </Box>
      {helperText && (
        <Box
          component="span"
          sx={{
            color: error ? '#f44336' : 'rgba(0, 0, 0, 0.6)',
            fontSize: '0.75rem',
            marginTop: '3px',
            marginLeft: '14px',
            display: 'block'
          }}
        >
          {helperText}
        </Box>
      )}
    </Box>
  );
};

export default PhoneNumberInput;